import React, { useState, useEffect } from 'react';
import { Modal, Input, message, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { useThemeSwitcher } from 'react-css-theme-switcher';

import doReturnTheme from '~src/styles/themeEngine';
import icon from '~components/_common/icon/icon';
import translate from '~src/i18n/translate';
import { ModalProps } from 'antd/lib/modal';
import { createCommit, deleteCommit, updateCommit } from '~src/services/api';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProjectId } from '~src/hooks/useProjectId';
import { DB_STATE } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { DbCommit, DbFolder } from '~src/types/db';
import {
  createTemplate,
  createTemplateFolder,
} from '~src/services/api/templates';
import moment from 'moment';

interface SaveTemplateModalType extends Partial<ModalProps> {
  state: boolean;
  // onSave: (templateName: string) => void;
}

const SaveTemplateModal = ({
  state,
  onCancel,
  ...modalProps
}: SaveTemplateModalType) => {
  const { currentTheme } = useThemeSwitcher();
  const [templateName, setTemplateName] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const theme = doReturnTheme(currentTheme);
  const intl = useIntl();

  const projectQuery = useProject();
  const projectId = useProjectId();
  const foldersQuery = useFolders({
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const onCreateTemplate = async (templateName) => {
    try {
      const newTemplate = await createTemplate({
        templateData: {
          name: templateName,
          start_date: moment().unix(),
          is_active: 0,
        },
        projectId: Number(projectId),
      });
      return newTemplate;
    } catch (err) {
      appLog('Error while creating template -> ', err);
      return undefined;
    }
  };

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: `Save template template for project with id = ${projectId}`,
      // TO DO: Check if important should be true/false
      is_important: true,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('Save Template -> createCommit -> commit', commitData);
      appLog('Save Template -> createCommit -> err', err);
      throw err;
    });
  };

  const onCreateTemplateFolders = (
    arr: DbFolder[],
    parrentId: DbFolder['parent_id'],
    newParrentId: DbFolder['parent_id'],
    commit_id: DbCommit['id'],
    templateId: number
  ): any[] => {
    return arr
      .filter((dbFolder) => dbFolder.parent_id === parrentId)
      .map(async (dbFolder) => {
        try {
          const createdFolder = await createTemplateFolder({
            folderData: {
              name: dbFolder.name,
              description: dbFolder.description,
              parent_id: newParrentId,
              state: dbFolder.state,
              is_locked: dbFolder.is_locked,
              is_favorite: dbFolder.is_favorite,
            },
            projectId: Number(projectId),
            templateId: templateId,
            commit_id: commit_id,
          });

          return Promise.all(
            onCreateTemplateFolders(
              arr,
              dbFolder.id,
              createdFolder.id,
              commit_id,
              templateId
            )
          );
        } catch (err) {
          appLog('createTemplateFolder -> err', err);
          message.error(intl.formatMessage({ id: 'folder-create-failed' }));
          // setIsLoading(false);
          throw err;
        }
      });
  };

  const onSaveTemplate = async (templateName: string) => {
    setIsLoading(true);
    const newTemplate = await onCreateTemplate(templateName);
    if (newTemplate) {
      const commit = await doCreateCommit();
      if (foldersQuery.data) {
        const results = await Promise.all(
          onCreateTemplateFolders(
            foldersQuery.data,
            null,
            null,
            commit.id,
            newTemplate.id
          )
        ).catch((foldersErr) => {
          deleteCommit({ projectId: String(projectId), commit }).catch(
            () => undefined
          );
          throw foldersErr;
        });

        if (results) {
          try {
            await updateCommit({
              projectId: String(projectId),
              commitData: { ...commit, state: DB_STATE.PROCESSED },
            });

            message.success(
              intl.formatMessage({ id: 'template-saved-successfully' })
            );
            // hide modal
            // @ts-ignore-line
            onCancel && onCancel();
          } catch (err) {
            appLog('Update commit err -> ', err);
          }
        }
      }
    }
  };

  const style = {
    container: {},
    titleBtn: {
      color: theme.colors.secondary,
      fontSize: '20px',
    },
    closeBtn: {
      color: theme.colors.darkGray,
      marginTop: '18px',
      fontSize: '20px',
      transition: '0.3s',
    },
  };
  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
          }}>
          {icon('far', 'folder-tree', style.titleBtn)}
          {intl.formatMessage({ id: 'save-template' })}
        </div>
      }
      centered
      visible={state}
      onOk={() => {
        templateName && onSaveTemplate(templateName);
      }}
      okButtonProps={{
        disabled: !templateName || templateName === '',
        loading: isLoading,
      }}
      afterClose={() => {
        setTemplateName(undefined);
        setIsLoading(false);
      }}
      onCancel={onCancel}
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      closeIcon={icon('far', 'times', style.closeBtn, 'close')}
      width={420}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <b>{intl.formatMessage({ id: 'template-name' })}</b>

        <Input
          placeholder={intl.formatMessage({ id: 'name-template-setup' })}
          allowClear
          onKeyPress={(e) =>
            e.key === 'Enter' && templateName && onSaveTemplate(templateName)
          }
          onChange={(e) => setTemplateName(e.target.value)}
          style={{
            margin: '4px 0px 15px -1px',
          }}
          value={templateName}
        />
      </div>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.25)',
            top: 0,
            left: 0,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Spin size="large" style={{ marginTop: '20%', marginLeft: '46%' }} />
        </div>
      )}
    </Modal>
  );
};

export default SaveTemplateModal;
